import React from "react";
import WebPlayerScrlr from "./WebPlayerScrlr";
import useDrag from "../../hooks/useDrag";
import { SCRLR_CONFIG } from "./config";
import { closeHelpPlayer, registerScrlrIconPlayerClose } from "./HelpPlayerModal";

const ScrlrIcon = () => {
  // All hooks must be called unconditionally
  const [isOpen, setIsOpen] = React.useState(false);
  const [dragStartPos, setDragStartPos] = React.useState(null);
  const [badgeCount, setBadgeCount] = React.useState(0);
  const [badgeVisible, setBadgeVisible] = React.useState(false);
  const hiddenIframeRef = React.useRef(null);
  const hiddenIframeTimeoutRef = React.useRef(null);
  const touchStartPosRef = React.useRef(null);
  const openedFromTouchRef = React.useRef(false);
  const suppressNextClickRef = React.useRef(false);
  const { position, isDragging, handleMouseDown, elementRef } = useDrag({
    topOffset: SCRLR_CONFIG.DRAG.TOP_OFFSET
  });

  const openPlayer = React.useCallback(() => {
    closeHelpPlayer();
    setIsOpen(true);
  }, []);

  React.useEffect(() => {
    return registerScrlrIconPlayerClose(() => setIsOpen(false));
  }, []);

  // Helper to remove hidden iframe
  const removeHiddenIframe = React.useCallback(() => {
    // Clear timeout if it exists
    if (hiddenIframeTimeoutRef.current) {
      clearTimeout(hiddenIframeTimeoutRef.current);
      hiddenIframeTimeoutRef.current = null;
    }
    
    if (hiddenIframeRef.current?.parentNode) {
      hiddenIframeRef.current.parentNode.removeChild(hiddenIframeRef.current);
      hiddenIframeRef.current = null;
    }
  }, []);

  // Helper to create hidden iframe
  const createHiddenIframe = React.useCallback(() => {
    if (hiddenIframeRef.current) return;
    const iframe = document.createElement('iframe');
    iframe.src = SCRLR_CONFIG.getHiddenUrl();
    iframe.style.cssText = SCRLR_CONFIG.HIDDEN_IFRAME_STYLES;
    iframe.setAttribute('aria-hidden', 'true');
    document.body.appendChild(iframe);
    hiddenIframeRef.current = iframe;
    
    // Set timeout to remove iframe if no message is received
    hiddenIframeTimeoutRef.current = setTimeout(() => {
      // Only remove if iframe still exists (hasn't been removed by postMessage)
      if (hiddenIframeRef.current) {
        removeHiddenIframe();
      }
    }, SCRLR_CONFIG.HIDDEN_IFRAME_TIMEOUT);
  }, [removeHiddenIframe]);

  // Always load hidden iframe on mount to get badge count
  React.useEffect(() => {
    createHiddenIframe();
    return removeHiddenIframe;
  }, [createHiddenIframe, removeHiddenIframe]);

  // Remove hidden iframe when modal opens (badge updates will come from modal iframe)
  // This prevents the hidden iframe from playing videos after modal interaction
  React.useEffect(() => {
    if (isOpen) {
      removeHiddenIframe();
    }
  }, [isOpen, removeHiddenIframe]);

  // Listen for badge updates and login events
  React.useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.type === 'scrlrBadgeUpdate') {
        if (event.data.source === 'hidden') {
          // Accept initial badge from hidden iframe, then remove it
          setBadgeCount(event.data.count || 0);
          setBadgeVisible(event.data.visible || false);
          removeHiddenIframe();
        } else {
          // Always accept updates from modal iframe
          setBadgeCount(event.data.count || 0);
          setBadgeVisible(event.data.visible || false);
        }
      } else if (event.data?.type === 'scrlrLoginSaved') {
        // Recreate hidden iframe after login to get badge (if it was removed)
        if (!hiddenIframeRef.current) {
          createHiddenIframe();
        }
      } else if (event.data?.type === 'scrlrLoginRequired') {
        // Keep hidden iframe when login is required (don't remove it yet)
        // It will send badge once user logs in
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [removeHiddenIframe, createHiddenIframe]);


  const handleIconMouseDown = (e) => {
    suppressNextClickRef.current = false;
    setDragStartPos({ x: e.clientX, y: e.clientY });
    handleMouseDown(e);
  };

  const markSuppressClickIfDragged = React.useCallback((startX, startY, endX, endY) => {
    const deltaX = Math.abs(endX - startX);
    const deltaY = Math.abs(endY - startY);
    const movedPastClickThreshold =
      deltaX >= SCRLR_CONFIG.DRAG.CLICK_THRESHOLD || deltaY >= SCRLR_CONFIG.DRAG.CLICK_THRESHOLD;

    if (movedPastClickThreshold) {
      suppressNextClickRef.current = true;
    }
  }, []);

  const tryOpenFromTap = React.useCallback((startX, startY, endX, endY, requireNotDragging = false) => {
    const deltaX = Math.abs(endX - startX);
    const deltaY = Math.abs(endY - startY);
    const withinThreshold = deltaX < SCRLR_CONFIG.DRAG.CLICK_THRESHOLD && deltaY < SCRLR_CONFIG.DRAG.CLICK_THRESHOLD;
    if (withinThreshold && (!requireNotDragging || !isDragging)) {
      openedFromTouchRef.current = true;
      openPlayer();
    }
  }, [isDragging, openPlayer]);

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    if (!t) return;
    suppressNextClickRef.current = false;
    touchStartPosRef.current = { x: t.clientX, y: t.clientY };
    setDragStartPos({ x: t.clientX, y: t.clientY });
    handleMouseDown({ clientX: t.clientX, clientY: t.clientY, preventDefault: () => {} });
  };

  const handleTouchEnd = (e) => {
    const t = e.changedTouches[0];
    const start = touchStartPosRef.current;
    touchStartPosRef.current = null;
    setDragStartPos(null);
    if (t && start) {
      markSuppressClickIfDragged(start.x, start.y, t.clientX, t.clientY);
      tryOpenFromTap(start.x, start.y, t.clientX, t.clientY, false);
    }
  };

  const handlePointerUp = (e) => {
    if (dragStartPos) {
      markSuppressClickIfDragged(dragStartPos.x, dragStartPos.y, e.clientX, e.clientY);
      if (e.pointerType !== 'mouse' && e.pointerType !== 'touch') {
        tryOpenFromTap(dragStartPos.x, dragStartPos.y, e.clientX, e.clientY, false);
      }
    }
    setDragStartPos(null);
  };

  const handleIconClick = (e) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      setDragStartPos(null);
      return;
    }
    if (openedFromTouchRef.current) {
      openedFromTouchRef.current = false;
      setDragStartPos(null);
      return;
    }
    if (dragStartPos) {
      const deltaX = Math.abs(e.clientX - dragStartPos.x);
      const deltaY = Math.abs(e.clientY - dragStartPos.y);
      if (deltaX < SCRLR_CONFIG.DRAG.CLICK_THRESHOLD && deltaY < SCRLR_CONFIG.DRAG.CLICK_THRESHOLD && !isDragging) {
        openPlayer();
      }
    } else {
      openPlayer();
    }
    setDragStartPos(null);
  };

  const closeWebPlayerScrlr = () => {
    setIsOpen(false);
  };

  return isOpen ? (
    <div className="webplayer-scrlr-modal">
      <WebPlayerScrlr 
        onClose={closeWebPlayerScrlr}
        initialPosition={position}
      />
    </div>
  ) : (
    <div 
      id="scrlrIcon"
      ref={elementRef}
      onMouseDown={handleIconMouseDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onPointerUp={handlePointerUp}
      onClick={handleIconClick}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
    >
      <img src={SCRLR_CONFIG.ICON_IMAGE_PATH} alt="Web Player Scrlr" />
      {badgeVisible && badgeCount > 0 && (
        <span className="scrlr-icon-badge">{badgeCount}</span>
      )}
    </div>
  );
}

export default ScrlrIcon;